import { Component, computed, OnInit, signal, ViewEncapsulation } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AccountApi, AccountMarketplace, AlgoMode, StrategyStateEnum } from "@front/m19-api-client";
import { StrategyEx } from "@front/m19-models";
import { AccountMarketplaceService, AccountSelectionService, AuthService, StrategyService } from "@front/m19-services";
import {
  ConfirmPopupComponent,
  IAlertComponent,
  IButtonComponent,
  IDatepickerComponent,
  IDateRange,
  IToggleComponent,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { Utils } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { GridLayoutComponent } from "@m19-board/containers/grid-layout/grid-layout.component";
import { StrategyOverlayComponent } from "@m19-board/overlay/strategy-overlay.component";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { StrategyBoostGridComponent } from "@m19-board/strategies/strategy-boost/strategy-boost-grid/strategy-boost-grid.component";
import { AmazonTimezoneService } from "@m19-board/utils/amazonTimezoneService";
import { ICON_IMPORT } from "@m19-board/utils/iconsLabels";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import moment from "moment-timezone";
import { forkJoin, of } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { BulkError, BulkImportService } from "../strategy-bulk-upload-modal/bulk-import.service";
import {
  StrategyBoostBulkReportComponent,
  StrategyBoostBulkReportData,
} from "./strategy-boost-bulk-upload/strategy-boost-bulk-report.component";
import {
  BoostUpdate,
  StrategyBoostBulkUploadComponent,
  StrategyBoostBulkUploadData,
} from "./strategy-boost-bulk-upload/strategy-boost-bulk-upload.component";

@UntilDestroy()
@Component({
  selector: "app-strategy-boost",
  templateUrl: "./strategy-boost.component.html",
  styleUrls: ["./strategy-boost.component.scss"],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    TranslocoDirective,
    StrategyOverlayComponent,
    IDatepickerComponent,
    ExportButtonComponent,
    IButtonComponent,
    StrategyBoostGridComponent,
    GridLayoutComponent,
    IToggleComponent,
    IAlertComponent,
  ],
})
export class StrategyBoostComponent implements OnInit {
  strategies: StrategyEx[] = [];
  accountMarketplace?: AccountMarketplace;
  filter: string = "";
  enabled = false;
  isReadOnly = false;

  user = toSignal(this.authService.loggedUser$);
  dateFormat = computed(() => Utils.getDateFormatString(this.user()?.locale ?? "en-US"));

  minDate = moment.utc();
  maxDate = moment.utc().add(10, "y");

  dateRange = signal<IDateRange | null>(null);

  readonly ICON_IMPORT = ICON_IMPORT;

  constructor(
    private accountSelectionService: AccountSelectionService,
    private strategyService: StrategyService,
    private authService: AuthService,
    private accountApi: AccountApi,
    private toasterService: ToasterService,
    private accountMarketplaceService: AccountMarketplaceService,
    private modalService: ModalService,
    private amazonTzService: AmazonTimezoneService,
    private bulkImportService: BulkImportService,
  ) {}

  date = moment.utc();

  ngOnInit(): void {
    const suggestedDate = new Date();
    suggestedDate.setDate(suggestedDate.getDate() + 5);

    this.accountSelectionService.readOnlyMode$.pipe(untilDestroyed(this)).subscribe((b) => (this.isReadOnly = b));

    this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(untilDestroyed(this)).subscribe((am) => {
      this.accountMarketplace = am;
      this.date = this.amazonTzService.getNow(am.marketplace);
      if (this.accountMarketplace.promoStartDate && this.accountMarketplace.promoEndDate) {
        this.enabled = true;
        this.dateRange.set({
          start: moment.utc(this.accountMarketplace.promoStartDate),
          end: moment.utc(this.accountMarketplace.promoEndDate),
        });
      } else {
        this.enabled = false;
        this.dateRange.set({ start: this.date, end: moment.utc(suggestedDate) });
      }
    });
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        switchMap((am) => this.strategyService.getStrategyIndex(am.accountId, am.marketplace)),
        untilDestroyed(this),
      )
      .subscribe((strategyIndex) => {
        this.strategies = [];
        for (const strategy of strategyIndex.values()) {
          const strategyEx = new StrategyEx(strategy);
          strategyEx.activatePrimeDayBoost = strategy.primeDayBoost != null;
          if (strategy.state != StrategyStateEnum.PAUSED) {
            this.strategies.push(strategyEx);
          }
        }
      });
  }

  toggleActivate(e: boolean) {
    if (!this.enabled) {
      this.update(this.dateRange());
      this.enabled = true;
    } else {
      const dateRange = this.dateRange();
      if (dateRange && this.date >= dateRange.start && dateRange.end && this.date <= dateRange.end) {
        const modalRef = this.modalService.openModal(ConfirmPopupComponent, {
          modalTitle: "",
          data: {
            message:
              "Promo days are currently running. ACOS Target of strategies with boost will go back to their initial values.",
            confirmLabel: "Deactivate Promo Days",
          },
        });
        modalRef.afterClosed().subscribe(() => {
          this.update();
          this.enabled = false;
        });
        return;
      } else {
        this.update();
        this.enabled = false;
      }
    }
  }

  update(dateRange?: IDateRange | null) {
    if (dateRange?.end && this.accountMarketplace) {
      const startDate = Utils.formatMomentDate(dateRange.start);
      const endDate = Utils.formatMomentDate(dateRange.end);

      this.dateRange.set(dateRange);

      const obj = {
        accountId: this.accountMarketplace.accountId,
        marketplace: this.accountMarketplace.marketplace,
        promoStartDate: startDate,
        promoEndDate: endDate,
        organizationId: this.accountMarketplace.resourceOrganizationId,
      };
      this.accountApi.updateAccountMarketplace(obj).subscribe((res) => {
        this.accountMarketplaceService.updateAccount(this.accountMarketplace!.accountId, (x) => {
          x.promoStartDate = startDate;
          x.promoEndDate = endDate;
        });
        this.accountSelectionService.updateAccountMarketplace([
          { ...this.accountMarketplace!, promoStartDate: startDate, promoEndDate: endDate },
        ]);
        this.toasterService.success("Promo days have been activated", "Promo Days Updated");
      });
    } else if (!dateRange) {
      const obj = {
        accountId: this.accountMarketplace!.accountId,
        marketplace: this.accountMarketplace!.marketplace,
        promoStartDate: "null",
        promoEndDate: "null",
        organizationId: this.accountMarketplace!.resourceOrganizationId,
      };
      this.accountApi.updateAccountMarketplace(obj).subscribe({
        next: (res) => {
          this.accountMarketplaceService.updateAccount(this.accountMarketplace!.accountId, (x) => {
            x.promoStartDate = undefined;
            x.promoEndDate = undefined;
          });
          this.accountSelectionService.updateAccountMarketplace([
            { ...this.accountMarketplace!, promoStartDate: undefined, promoEndDate: undefined },
          ]);
          this.toasterService.success("Promo days have been deactivated", "Promo Days Updated");
        },
        error: (err) => this.toasterService.error(err.response.message, "Promo Days Update Error"),
      });
    }
  }

  uploadPromoDays() {
    const ref = this.modalService.openModal<
      StrategyBoostBulkUploadData,
      { updates: BoostUpdate[]; errors: BulkError[] }
    >(StrategyBoostBulkUploadComponent, {
      data: {
        strategyIndex: new Map(this.strategies.map((s) => [s.strategyId, s])),
      },
    });
    ref
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        switchMap(({ updates, errors }) => {
          if (updates.length == 0) {
            return of({ updates, errors });
          }
          return forkJoin(
            updates.map((update) =>
              this.strategyService
                .updatePrimeDayBoost(
                  this.accountMarketplace!.accountId,
                  this.accountMarketplace!.marketplace,
                  this.accountMarketplace!.resourceOrganizationId!,
                  update.strategyId,
                  update.boost,
                )
                .pipe(take(1)),
            ),
          ).pipe(map(() => ({ updates, errors })));
        }),
      )
      .subscribe({
        next: ({ updates, errors }) => {
          this.modalService.openModal<StrategyBoostBulkReportData, void>(StrategyBoostBulkReportComponent, {
            data: {
              boostUpdates: updates,
              errors,
            },
          });
        },
        error: (err) => {
          this.toasterService.error(err.response.message, "Promo Days Update Error");
        },
      });
  }

  exportAsCsv() {
    this.bulkImportService.exportPromoDaysBoostCsv(
      this.accountMarketplace!,
      // do not export unboostable strategies
      this.strategies.filter((s) => s.algoMode == AlgoMode.ACOS_TARGET && !s.dailyBudget),
    );
  }
}
